import { bind, SUSPENSE } from "@react-rxjs/core"
import { switchMapSuspended } from "@react-rxjs/utils"
import { combineLatest, scan, switchMap } from "rxjs"

import {
  currentSymbol$,
  periodicity$,
} from "@/App/Equities/shared/components/chart/state"
import { type BarResponse } from "@/App/Equities/shared/services/TradingGateway"
import { TimeUnit } from "@/App/Equities/shared/utils"
import { assertNever } from "@/utils"

import { mergeLiveBar } from "./barBucketing"
import { latestBars$ } from "./latestQuote"
import { getHistoricalBars$ } from "./marketData"

const MS_PER_DAY = 24 * 60 * 60 * 1_000

function getStartTime(endTime: Date, timeFrame: TimeUnit) {
  switch (timeFrame) {
    case TimeUnit.Min:
      return new Date(endTime.getTime() - 5 * MS_PER_DAY)
    case TimeUnit.Hour:
      return new Date(endTime.getTime() - 100 * MS_PER_DAY)
    case TimeUnit.Day:
      return new Date(endTime.getTime() - 1_000 * MS_PER_DAY)
    default:
      assertNever(timeFrame)
  }
}

export const [useHistoricalBars, historicalBars$] = bind<
  BarResponse[] | typeof SUSPENSE
>(
  combineLatest([currentSymbol$, periodicity$]).pipe(
    switchMapSuspended(([currentSymbol, periodicity]) => {
      const dateNow = new Date()
      const historicals$ = getHistoricalBars$({
        symbols: currentSymbol,
        startTime: getStartTime(dateNow, periodicity.timeUnit).toISOString(),
        endTime: dateNow.toISOString(),
        limit: 500,
        timeframe: `${periodicity.interval}${periodicity.timeUnit}`,
      })

      // scan, not map: a live candle accumulates over its bucket. Folding each
      // tick into the previous result keeps buckets that opened after the
      // historicals were fetched, and keeps the open candle's high and low
      // across the whole bucket rather than just the latest tick.
      return historicals$.pipe(
        switchMap((bars) =>
          latestBars$(currentSymbol).pipe(
            scan(
              (candles, liveBar) =>
                mergeLiveBar(
                  candles,
                  liveBar,
                  currentSymbol,
                  periodicity.timeUnit,
                  periodicity.interval,
                ),
              bars,
            ),
          ),
        ),
      )
    }),
  ),
)
